import { EventEmitter } from "node:events";
import type { LogEvent } from "@better-ccflare/types";
import { logFileWriter } from "./file-writer";

export enum LogLevel {
	DEBUG = 0,
	INFO = 1,
	WARN = 2,
	ERROR = 3,
}

export type LogFormat = "pretty" | "json";

// Event emitter for log streaming
export const logBus = new EventEmitter();

// Set a more generous max listeners limit for SSE connections
// This allows for more concurrent SSE connections while still providing protection
logBus.setMaxListeners(200);

// Error's name/message/stack are non-enumerable, so JSON.stringify(err) returns "{}".
// Convert Errors to plain objects before they flow into formatMessage / LogEvent /
// the file writer, which all use JSON.stringify downstream.
// biome-ignore lint/suspicious/noExplicitAny: payload is intentionally untyped
function normalizeLogData(data: any): any {
	if (data === undefined || data === null) return data;
	if (data instanceof Error) {
		const out: Record<string, unknown> = {
			name: data.name,
			message: data.message,
			stack: data.stack,
		};
		if (data.cause !== undefined) {
			out.cause =
				data.cause instanceof Error ? normalizeLogData(data.cause) : data.cause;
		}
		return out;
	}
	return data;
}

// String(e) can itself throw (e.g. an object with a throwing
// Symbol.toPrimitive, or Object.create(null)), which would defeat the
// purpose of the catch block calling this. Never let this throw.
function safeErrorReason(e: unknown): string {
	if (e instanceof Error) return e.message;
	try {
		return String(e);
	} catch {
		return "unknown error";
	}
}

let consoleLoggingOverride: boolean | null = null;

/**
 * Force console output on (or off) for every logger, regardless of debug
 * mode. The interactive TUI silences console logging so log lines do not
 * corrupt the screen, but headless serve mode has no TUI: journald or the
 * operator's terminal is exactly where WARN/ERROR lines belong. Without
 * this, production warnings (runaway fan-out, session budgets) are
 * invisible outside the dashboard log bus.
 */
export function setConsoleLogging(enabled: boolean | null): void {
	consoleLoggingOverride = enabled;
}

export class Logger {
	private level: LogLevel;
	private prefix: string;
	private format: LogFormat;
	private silentConsole: boolean;

	constructor(prefix: string = "", level: LogLevel = LogLevel.INFO) {
		this.prefix = prefix;
		this.level = this.getLogLevelFromEnv() ?? level;
		this.format = this.getFormatFromEnv();
		// Only show console output in debug mode or if BETTER_CCFLARE_DEBUG or legacy ccflare_DEBUG is set
		this.silentConsole = !(
			this.isDebugEnabled() || this.level === LogLevel.DEBUG
		);
	}

	private getLogLevelFromEnv(): LogLevel | null {
		// Check if we're in a Node.js environment
		if (typeof process === "undefined" || !process.env) {
			return null;
		}
		const envLevel = process.env.LOG_LEVEL?.toUpperCase();
		if (envLevel && envLevel in LogLevel) {
			return LogLevel[envLevel as keyof typeof LogLevel];
		}
		return null;
	}

	private getFormatFromEnv(): LogFormat {
		// Check if we're in a Node.js environment
		if (typeof process === "undefined" || !process.env) {
			return "pretty";
		}
		return (process.env.LOG_FORMAT as LogFormat) || "pretty";
	}

	private isDebugEnabled(): boolean {
		// Check if we're in a Node.js environment
		if (typeof process === "undefined" || !process.env) {
			return false;
		}
		return (
			process.env.BETTER_CCFLARE_DEBUG === "1" ||
			process.env.ccflare_DEBUG === "1"
		);
	}

	// biome-ignore lint/suspicious/noExplicitAny: Logger needs to accept any data type
	private formatMessage(level: string, message: string, data?: any): string {
		const timestamp = new Date().toISOString();

		if (this.format === "json") {
			const logEntry = {
				ts: timestamp,
				level,
				prefix: this.prefix || undefined,
				msg: message,
				...(data && { data }),
			};
			try {
				return JSON.stringify(logEntry);
			} catch (e: unknown) {
				// data is caller-supplied and may be circular or contain a
				// BigInt, both of which make JSON.stringify throw. A logging
				// call must never crash its caller, so fall back to a
				// sanitized entry that preserves ts/level/msg.
				const reason = safeErrorReason(e);
				return JSON.stringify({
					ts: timestamp,
					level,
					prefix: this.prefix || undefined,
					msg: message,
					data: `[unserializable: ${reason}]`,
				});
			}
		} else {
			const prefix = this.prefix ? `[${this.prefix}] ` : "";
			let dataStr = "";
			if (data) {
				try {
					dataStr = ` ${JSON.stringify(data)}`;
				} catch (e: unknown) {
					dataStr = ` [unserializable: ${safeErrorReason(e)}]`;
				}
			}
			return `[${timestamp}] ${level}: ${prefix}${message}${dataStr}`;
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: Logger needs to accept any data type
	debug(message: string, data?: any): void {
		if (this.level <= LogLevel.DEBUG) {
			const normalized = normalizeLogData(data);
			const event: LogEvent = {
				ts: Date.now(),
				level: "DEBUG",
				msg: message,
				...(normalized !== undefined && { data: normalized }),
			};
			if (logBus.listenerCount("log") > 0) logBus.emit("log", event);
			logFileWriter?.write(event);
			if (consoleLoggingOverride ?? !this.silentConsole) {
				console.log(this.formatMessage("DEBUG", message, normalized));
			}
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: Logger needs to accept any data type
	info(message: string, data?: any): void {
		if (this.level <= LogLevel.INFO) {
			const normalized = normalizeLogData(data);
			const event: LogEvent = {
				ts: Date.now(),
				level: "INFO",
				msg: message,
				...(normalized !== undefined && { data: normalized }),
			};
			if (logBus.listenerCount("log") > 0) logBus.emit("log", event);
			logFileWriter?.write(event);
			if (consoleLoggingOverride ?? !this.silentConsole) {
				console.log(this.formatMessage("INFO", message, normalized));
			}
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: Logger needs to accept any data type
	warn(message: string, data?: any): void {
		if (this.level <= LogLevel.WARN) {
			const normalized = normalizeLogData(data);
			const event: LogEvent = {
				ts: Date.now(),
				level: "WARN",
				msg: message,
				...(normalized !== undefined && { data: normalized }),
			};
			if (logBus.listenerCount("log") > 0) logBus.emit("log", event);
			logFileWriter?.write(event);
			if (consoleLoggingOverride ?? !this.silentConsole) {
				console.warn(this.formatMessage("WARN", message, normalized));
			}
		}
	}

	// biome-ignore lint/suspicious/noExplicitAny: Logger needs to accept any error type
	error(message: string, error?: any): void {
		if (this.level <= LogLevel.ERROR) {
			const normalized = normalizeLogData(error);
			const event: LogEvent = {
				ts: Date.now(),
				level: "ERROR",
				msg: message,
				...(normalized !== undefined && { data: normalized }),
			};
			if (logBus.listenerCount("log") > 0) logBus.emit("log", event);
			logFileWriter?.write(event);
			if (consoleLoggingOverride ?? !this.silentConsole) {
				console.error(this.formatMessage("ERROR", message, normalized));
			}
		}
	}

	setLevel(level: LogLevel): void {
		this.level = level;
		// Update silentConsole when level changes
		this.silentConsole = !(
			this.isDebugEnabled() || this.level === LogLevel.DEBUG
		);
	}

	getLevel(): LogLevel {
		return this.level;
	}
}

// Default logger instance
export const log = new Logger();
export { logFileWriter } from "./file-writer";
